import { get, isEmpty, omit } from 'lodash';
import {
  SURVEY_TYPE_SIMILAR_ON_EXPIRE,
  SURVEY_TYPE_ADDITIONAL_ON_EXPIRE,
  PARTICIPATION_STATUS,
} from '../../constants';
// eslint-disable-next-line import/no-cycle
import Survey from './index';
import request from '../../request';

const createNewSurvey = (survey) => {
  /* eslint-disable camelcase */
  const {
    lang,
    user_lang,
    translations,
    s_id: surveyId,
    tag_questions,
  } = survey;
  /* eslint-enable camelcase */
  if (!Pollfish.helper.json.translations[lang]) {
    Pollfish.helper.addTranslation({ lang, texts: translations });
  }

  // if there are still demographics to be answered, then we need to ask
  // them on the user_lang. The backend will also send them as the user_lang
  // This is implied due to business logic and not the response.
  // We should update this in the future to contain the language expected by tags
  if (!isEmpty(tag_questions)) {
    Pollfish.helper.changeLanguage(user_lang);
    // if a demographic question is not properly saved (HTTP-503)
    // then we might get it again after the survey enter
  } else {
    Pollfish.helper.changeLanguage(lang);
  }

  // Survey Viewed events are being handled by different integrations in different ways
  // We handle survey viewed here because surveys coming from the check status
  // will never be handled by each integration and we need to override them
  Pollfish.mobile.interface.sendToServer(
    '/v2/device/set/survey/viewed',
    `{"s_id": "${surveyId}"}`,
    false,
  );
  Pollfish.survey = new Survey(survey, { checkStatus: false });
  Pollfish.survey.start().catch(Pollfish.helper.handleError);
};

// if a survey has no id, there is no reason to check for its status
// This can happen on the unlock screen of the offerwall where no s_id is provided
export const shouldCheckStatus = () =>
  Pollfish.helper.json.s_id &&
  !Pollfish.helper.isPreview &&
  !Pollfish.helper.isDebugMode() &&
  !Pollfish.store.disableSurveyStatusCheck;

const asyncCheckAndEnterSurvey = async ({
  tagAnswers = [],
  profilingAnswers = [],
  isUserNotEligible,
  onSurveyContinue,
}) => {
  if (!shouldCheckStatus()) {
    onSurveyContinue();
    return;
  }
  const surveyId = Pollfish.helper.json.s_id;
  Pollfish.ui.showSurveyLoading();

  const answersTransformerFunction = (saveAnswerObject) => ({
    q_id: saveAnswerObject.q_id,
    answers: saveAnswerObject.answers.map((answer) =>
      omit(answer, ['answerOrder']),
    ),
  });
  const cleanTagAnswers = tagAnswers.map(answersTransformerFunction);
  const cleanProfilingAnswers = profilingAnswers.map(
    answersTransformerFunction,
  );
  const {
    survey_response_type: surveyType,
    survey_session_id: surveySessionId,
    taxonomy,
    success,
    survey,
  } = await request({
    url: `/v2/surveys/${surveyId}/enter-survey`,
    method: 'POST',
    data: {
      tag_answers: cleanTagAnswers,
      profiling_answers: cleanProfilingAnswers,
      isUserNotEligible,
    },
  });
  // TODO: investigation only, remove after testing
  // all enter survey responses should contain survey_session_id
  if (!surveySessionId) {
    Raven.captureMessage('Missing survey session id', {
      extra: {
        ...Pollfish.helper.getRedirectData(),
      },
    });
  }
  // There is a case that while enter-survey has not finished
  // respondent is allowed to reject the survey which means that
  // the active survey will be a feedback survey, and should not
  // changed again to show a sanity question
  if (Pollfish.survey && Pollfish.survey.isFeedbackSurvey()) {
    return;
  }

  // all enter survey responses should contain survey_session_id
  // even if respondent is not eligible to continue, this change
  // has been made for fraudo and straight-lining
  if (Pollfish.survey) {
    Pollfish.survey.setSurveySessionId(surveySessionId);
  } else {
    Raven.captureMessage('Unexpected flow: could not find existing survey', {
      extra: {
        ...Pollfish.helper.getRedirectData(),
      },
    });
  }

  if (success) {
    // One multiple audiences we are checking only one audience
    // so maybe user is eligible in another audience so
    // reset to false the disqualified flags
    Pollfish.demographics.setDisqualified(false);
    Pollfish.profiling.setDisqualified(false);
    if (Pollfish.survey) {
      // enter-survey response may give another 3rdParty redirect url
      const updateRedirectUrl = get(survey, 'url');
      Pollfish.survey.setSurveyRedirectUrl(updateRedirectUrl);
      // many choice sets are called a block on the backend though. Might need to change
      // the naming here
      Pollfish.store.addTaxonomyData(taxonomy);
    } else {
      Raven.captureMessage(
        'Unexpected flow: check or get similar has no survey',
      );
    }
    onSurveyContinue();
    return;
  }

  const containsSurvey = get(survey, 'containsSurvey', false);
  if (!containsSurvey) {
    Pollfish.helper.handleSurveyComplete({
      isUserNotEligible: true,
      disqualificationReason: PARTICIPATION_STATUS.SURVEY_EXPIRED,
      shouldGetInRouter: false,
      shouldCallSurveyComplete: false,
    });
    return;
  }

  if (surveyType === SURVEY_TYPE_SIMILAR_ON_EXPIRE) {
    createNewSurvey({
      ...survey,
      survey_session_id: surveySessionId,
      userTaxonomy: taxonomy,
    });
  } else if (surveyType === SURVEY_TYPE_ADDITIONAL_ON_EXPIRE) {
    Pollfish.ui.showSurveyExpiredScreen(() => {
      createNewSurvey({
        ...survey,
        survey_session_id: surveySessionId,
        userTaxonomy: taxonomy,
      });
    });
  }
};

const checkAndEnterSurvey = (props) => {
  asyncCheckAndEnterSurvey(props).catch(
    Pollfish.helper.handleGlobalPromiseError,
  );
};

export default checkAndEnterSurvey;
